/* /akademi + /fiyatlandirma — public marketing pages */
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Star, Trophy, Users, GraduationCap, Heart,
  Activity, Brain, Calendar, Sparkles, Crown, Zap, Phone,
  MessageCircle, ShieldCheck, TrendingUp, X,
} from "lucide-react";
import MarketingLayout, { SmartImg } from "../../components/marketing/MarketingLayout";

const IMG = "/marketing-v2";
const PHONE_INTL = "905437934101";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: delay * 0.07, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const PageHero = ({ over, title, accent, sub }) => (
  <section className="relative pt-36 pb-20 px-6 lg:px-10 mkt-glow-yellow overflow-hidden">
    <div className="max-w-[1400px] mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="font-over text-dr-yellow mb-5">{over}</motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
        className="font-hero uppercase text-[44px] md:text-[80px] lg:text-[100px] leading-[0.92] mb-6 max-w-5xl"
      >
        {title} {accent && <span className="text-dr-yellow">{accent}</span>}
      </motion.h1>
      {sub && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="text-lg md:text-xl text-white/70 max-w-3xl leading-relaxed">{sub}</motion.p>
      )}
    </div>
  </section>
);

/* ============================================================
   AKADEMİ SAYFASI
   ============================================================ */
const ACADEMY_PILLARS = [
  [Trophy, "PROFESYONEL ANTRENÖR KADROSU", "UEFA lisanslı antrenörler · yaş grubu uzmanlığı · sürekli gelişim"],
  [Brain, "YAPAY ZEKA DESTEKLİ TAKİP", "Çocuğunuzun gelişimi DR AI ile haftalık raporlanır — siz de görürsünüz"],
  [Heart, "KARAKTER + DİSİPLİN", "Sahada ve dışında. Saygı, takım ruhu, çalışkanlık temel değerlerimiz"],
  [Activity, "BİREYSEL GELİŞİM", "Her çocuk farklı. Her çocuğa özel hedef, ölçüm, geri bildirim"],
  [ShieldCheck, "GÜVENLİK ÖNCE GELİR", "Sertifikalı saha · ilk yardım eğitimli antrenörler · sigortalı antrenmanlar"],
  [Users, "ARKADAŞLIK + AİDİYET", "Çocuğunuz takımının bir parçası — ömür boyu sürecek dostluklar burada başlar"],
];

const ACADEMY_GROUPS = [
  { age: "U10", label: "ALTYAPI (8-10 YAŞ)", desc: "Top sevgisi · temel beceri · takım kavramı", days: "PZT · ÇRŞ · CMA", color: "from-dr-yellow/30 to-dr-yellow/5" },
  { age: "U12", label: "GELİŞİM (10-12 YAŞ)", desc: "Teknik temel · pas-şut-kontrol · pozisyon", days: "PZT · ÇRŞ · CMA", color: "from-dr-yellow/40 to-dr-yellow/5" },
  { age: "U14", label: "TAKTİK (12-14 YAŞ)", desc: "Modern futbol taktiği · oyun anlayışı · rekabet", days: "PZT · SAL · ÇRŞ · CMA", color: "from-dr-yellow/50 to-dr-yellow/10" },
  { age: "U17", label: "PROFESYONELLİK (14-17 YAŞ)", desc: "Yarışmacı seviye · ulusal turnuvalar · scout takibi", days: "Hafta 5 gün", color: "from-dr-yellow/60 to-dr-yellow/10" },
];

const ACADEMY_TESTIMONIALS = [
  {
    name: "Mehmet Y.", role: "Babası · 11 yaş", text: "Oğlumun sahaya değil, hayata hazırlandığını gördüm. Antrenörlerin ilgisi inanılmaz, her hafta gelişim raporu alıyoruz. Tek pişmanlığım daha erken başlatmamış olmak.",
  },
  {
    name: "Esra K.", role: "Annesi · 9 yaş", text: "Saygı, sorumluluk, takım ruhu. Bunları okul öğretmiyor — burada öğreniyor. Üstelik futbolu da seviyor 😊",
  },
  {
    name: "Ali D.", role: "Babası · 14 yaş", text: "Oğlum geçen sezon U14 takımında 12 gol attı. Şimdi profesyonel kulüplerden ilgi görüyor. Bu kapıyı bize DR AI Futbol açtı.",
  },
];

const AcademyApplyForm = () => {
  const [form, setForm] = useState({
    cocuk_ad_soyad: "", cocuk_yas: "", cocuk_pozisyon: "",
    veli_ad_soyad: "", telefon: "", email: "", il_ilce: "", not_metni: "",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ad_soyad: form.veli_ad_soyad,
          kulup_adi: `Akademi başvurusu — ${form.cocuk_ad_soyad} (${form.cocuk_yas} yaş, ${form.cocuk_pozisyon || "-"})`,
          telefon: form.telefon,
          email: form.email,
          mesaj: `Çocuk: ${form.cocuk_ad_soyad}\nYaş: ${form.cocuk_yas}\nPozisyon: ${form.cocuk_pozisyon}\nİl/İlçe: ${form.il_ilce}\nNot: ${form.not_metni}`,
          kaynak: "akademi-basvuru",
        }),
      });
      if (!res.ok) throw new Error("Gönderilemedi");
      setSent(true);
    } catch (e2) {
      setErr("Hata: " + e2.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-14">
        <div className="w-16 h-16 rounded-full bg-dr-yellow text-dr-black flex items-center justify-center mx-auto mb-5">
          <Check size={30} strokeWidth={3} />
        </div>
        <h3 className="font-hero text-3xl uppercase mb-3">Başvurunuz Alındı!</h3>
        <p className="text-white/70 max-w-md mx-auto">
          Antrenör koordinatörümüz <strong className="text-dr-yellow">24 saat içinde</strong> sizi arayacak ve deneme antrenmanı için randevu verilecektir.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="academy-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required type="text" placeholder="Çocuğun Adı Soyadı *" value={form.cocuk_ad_soyad}
          onChange={(e) => setForm({ ...form, cocuk_ad_soyad: e.target.value })}
          className="bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
          data-testid="academy-input-cocuk-ad" />
        <input required type="number" min="6" max="18" placeholder="Yaş *" value={form.cocuk_yas}
          onChange={(e) => setForm({ ...form, cocuk_yas: e.target.value })}
          className="bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
          data-testid="academy-input-yas" />
      </div>
      <select value={form.cocuk_pozisyon}
        onChange={(e) => setForm({ ...form, cocuk_pozisyon: e.target.value })}
        className="w-full bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-dr-yellow focus:outline-none"
        data-testid="academy-input-pozisyon"
      >
        <option value="">Tercih ettiği pozisyon (opsiyonel)</option>
        <option>Kaleci</option><option>Defans</option><option>Orta Saha</option>
        <option>Forvet</option><option>Bilmiyorum / Çocuk Karar Versin</option>
      </select>

      <div className="border-t border-white/5 pt-3 mt-2">
        <div className="font-over text-dr-yellow mb-3">VELİ BİLGİLERİ</div>
        <div className="space-y-3">
          <input required type="text" placeholder="Veli Adı Soyadı *" value={form.veli_ad_soyad}
            onChange={(e) => setForm({ ...form, veli_ad_soyad: e.target.value })}
            className="w-full bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
            data-testid="academy-input-veli" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input required type="tel" placeholder="Telefon (WhatsApp) *" value={form.telefon}
              onChange={(e) => setForm({ ...form, telefon: e.target.value })}
              className="bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
              data-testid="academy-input-tel" />
            <input type="email" placeholder="E-posta (opsiyonel)" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
              data-testid="academy-input-email" />
          </div>
          <input type="text" placeholder="İl / İlçe" value={form.il_ilce}
            onChange={(e) => setForm({ ...form, il_ilce: e.target.value })}
            className="w-full bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
            data-testid="academy-input-ilce" />
          <textarea rows={2} placeholder="Eklemek istediğiniz bir not? (opsiyonel)"
            value={form.not_metni} onChange={(e) => setForm({ ...form, not_metni: e.target.value })}
            className="w-full bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
            data-testid="academy-input-not" />
        </div>
      </div>

      {err && <div className="text-dr-risk text-sm">{err}</div>}
      <button disabled={loading}
        className="mkt-btn-primary w-full justify-center !py-3.5 disabled:opacity-60"
        data-testid="academy-submit"
      >
        {loading ? "Gönderiliyor..." : "Ücretsiz Deneme Antrenmanı İste"}
        {!loading && <ArrowRight size={18} strokeWidth={3} />}
      </button>
      <p className="text-xs text-white/45 text-center mt-3">
        🛡️ Hiçbir baskı yok — antrenör koordinatörümüz arayıp uygun gün belirler. Kayıt yok, taahhüt yok.
      </p>
    </form>
  );
};

export const AcademyPage = () => (
  <MarketingLayout>
    <PageHero
      over="DR AI FUTBOL AKADEMİSİ"
      title="ÇOCUĞUNUZUN"
      accent="YILDIZLAŞMA YOLCULUĞU"
      sub="Profesyonel antrenör kadrosu, yapay zeka destekli gelişim takibi ve karakter eğitimi — Türkiye'nin en yenilikçi futbol akademisinde, çocuğunuzun en iyi versiyonu doğuyor."
    />

    {/* Stats banner */}
    <section className="px-6 lg:px-10 -mt-8 pb-16">
      <div className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["120+", "AKTİF SPORCU"],
          ["8", "PROFESYONEL ANTRENÖR"],
          ["4", "YAŞ GRUBU"],
          ["%92", "VELİ MEMNUNİYETİ"],
        ].map(([n, lbl], i) => (
          <Reveal key={lbl} delay={i}>
            <div className="text-center mkt-card rounded-2xl p-6">
              <div className="font-hero text-4xl text-dr-yellow">{n}</div>
              <div className="font-over text-white/60 mt-2">{lbl}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Pillars */}
    <section className="px-6 lg:px-10 py-20 bg-dr-ink-1/30">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="font-over text-dr-yellow mb-3">NEDEN BİZ</div>
          <h2 className="mkt-h2">
            6 SEBEP — <span className="text-dr-yellow">ÇOCUĞUNUZ</span><br />
            BURADA OLMALI
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ACADEMY_PILLARS.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i % 3}>
              <div className="mkt-card rounded-2xl p-7 h-full" data-testid={`pillar-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-dr-yellow/10 border border-dr-yellow/20 flex items-center justify-center mb-5">
                  <Icon size={22} className="text-dr-yellow" strokeWidth={2} />
                </div>
                <h3 className="font-hero text-base uppercase mb-2">{t}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Age groups */}
    <section className="px-6 lg:px-10 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-3xl mb-12">
          <div className="font-over text-dr-yellow mb-3">YAŞ GRUPLARI</div>
          <h2 className="mkt-h2">
            HER YAŞA<br />
            <span className="text-dr-yellow">ÖZEL PROGRAM</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ACADEMY_GROUPS.map((g, i) => (
            <Reveal key={g.age} delay={i}>
              <div className={`relative rounded-2xl p-7 h-full bg-gradient-to-br ${g.color} border border-white/10 hover:border-dr-yellow/40 transition`} data-testid={`age-${g.age}`}>
                <div className="font-hero text-5xl text-dr-yellow mb-2">{g.age}</div>
                <div className="font-over text-white mb-3">{g.label}</div>
                <p className="text-sm text-white/70 leading-relaxed mb-4">{g.desc}</p>
                <div className="flex items-center gap-2 text-xs text-white/60 pt-3 border-t border-white/10">
                  <Calendar size={13} className="text-dr-yellow" />
                  {g.days}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Visual + AI takip */}
    <section className="px-6 lg:px-10 py-20 bg-dr-ink-1/30">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <SmartImg src={`${IMG}/academy-kids.png`} alt="DR AI Futbol Akademi" className="w-full rounded-2xl border border-white/10 shadow-2xl" />
        </Reveal>
        <div>
          <div className="font-over text-dr-yellow mb-3">YAPAY ZEKA DESTEKLİ GELİŞİM</div>
          <h2 className="mkt-h2 mb-6">
            ÇOCUĞUNUZUN GELİŞİMİNİ<br />
            <span className="text-dr-yellow">HAFTALIK GÖRÜN</span>
          </h2>
          <p className="text-lg text-white/70 leading-relaxed mb-8">
            Antrenörümüz her antrenman sonrası çocuğunuzun durumunu DR AI Engine'e işler.
            Hafta sonu size <strong className="text-white">WhatsApp'tan gelişim raporu</strong> gönderilir.
          </p>
          <ul className="space-y-3">
            {[
              "Devamlılık & disiplin yüzdesi",
              "Teknik beceri puanları (pas, şut, kontrol, dribling)",
              "Takım içi etkileşim & liderlik",
              "Önerilen ev egzersizleri",
              "Antrenörün kişisel notu",
            ].map((x) => (
              <li key={x} className="flex gap-3 text-white/85">
                <Check size={18} className="text-dr-yellow shrink-0 mt-1" strokeWidth={3} />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* Testimonials */}
    <section className="px-6 lg:px-10 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-3xl mb-12">
          <div className="font-over text-dr-yellow mb-3">VELİLERİMİZ NE DİYOR?</div>
          <h2 className="mkt-h2">
            EN İYİ REKLAM<br />
            <span className="text-dr-yellow">ANNE-BABA YORUMU</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {ACADEMY_TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i}>
              <div className="mkt-card rounded-2xl p-7 h-full flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} size={16} className="fill-dr-yellow text-dr-yellow" />
                  ))}
                </div>
                <p className="text-white/85 leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="mt-auto pt-4 border-t border-white/5">
                  <div className="font-hero uppercase text-sm">{t.name}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Application form */}
    <section className="px-6 lg:px-10 py-20 bg-dr-ink-1/30">
      <div className="max-w-[1100px] mx-auto grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2">
          <div className="font-over text-dr-yellow mb-3">ÜCRETSİZ DENEME</div>
          <h2 className="mkt-h2 mb-6">
            BAŞVUR,<br />
            <span className="text-dr-yellow">DENEME</span><br />
            ANTRENMANI AL
          </h2>
          <p className="text-white/70 leading-relaxed mb-6">
            Bir hafta boyunca çocuğunuz <strong className="text-white">ücretsiz</strong> antrenmanlarımıza katılabilir.
            Karar sizin — beğenirseniz devam, beğenmezseniz hayır deyin.
          </p>
          <div className="space-y-3 text-sm text-white/70">
            <div className="flex items-center gap-3"><Check size={16} className="text-dr-yellow" strokeWidth={3} /> 1 hafta boyunca ücretsiz</div>
            <div className="flex items-center gap-3"><Check size={16} className="text-dr-yellow" strokeWidth={3} /> Hiçbir taahhüt / ön ödeme yok</div>
            <div className="flex items-center gap-3"><Check size={16} className="text-dr-yellow" strokeWidth={3} /> 24 saat içinde geri arama</div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`https://wa.me/${PHONE_INTL}?text=${encodeURIComponent("Merhaba, akademi için bilgi almak istiyorum")}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1ebe57] transition"
              data-testid="academy-wa">
              <MessageCircle size={16} /> WhatsApp
            </a>
            <a href={`tel:+${PHONE_INTL}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white font-semibold text-sm hover:border-dr-yellow hover:text-dr-yellow transition"
              data-testid="academy-tel">
              <Phone size={16} /> +90 543 793 41 01
            </a>
          </div>
        </div>
        <div className="lg:col-span-3 mkt-card rounded-2xl p-7 lg:p-9">
          <h3 className="font-hero text-2xl uppercase mb-6">Başvuru Formu</h3>
          <AcademyApplyForm />
        </div>
      </div>
    </section>
  </MarketingLayout>
);

/* ============================================================
   FİYATLANDIRMA SAYFASI
   ============================================================ */
const PLANS = [
  {
    id: "starter", name: "STARTER", tagline: "Yeni başlayanlar için", price: 999, credits: 25, icon: Zap, cta: "Ücretsiz Üye Ol",
    features: [
      ["Kurumsal Web Sitesi (Pro'ya özel)", false],
      ["Özel Domain + SSL (Pro'ya özel)", false],
      ["20+ Modül Panel Erişimi", true],
      ["4 AI Instagram Şablonu", true],
      ["Aylık 25 AI Kredi", true],
      ["Manuel Lig Verisi Sync", true],
      ["Otomatik Puan Durumu", true],
      ["Antrenman & Yoklama", true],
      ["Kulüp Maliyesi", true],
      ["AI Maç Analizi (DR AI Engine)", false],
      ["Akademi Yönetimi", false],
      ["Otomatik Caption (DR AI)", false],
      ["Öncelikli Destek", false],
      ["Özel Eğitim Seansı", false],
      ["Kulübünüzün veri girişi", false],
      ["Oyuncu veri girişi", false],
      ["Gelecekteki özelliklere öncelikli erişim", false],
    ],
  },
  {
    id: "plus",
    name: "PLUS",
    tagline: "En çok tercih edilen",
    price: 4999,
    credits: 75,
    icon: Crown,
    badge: "EN POPÜLER",
    highlight: true,
    cta: "Hemen Ücretsiz Üye Ol",
    features: [
      ["Kurumsal Web Sitesi (Pro'ya özel)", false],
      ["Özel Domain + SSL (Pro'ya özel)", false],
      ["20+ Modül Panel Erişimi", true],
      ["9 AI Instagram Şablonu", true],
      ["Aylık 75 AI Kredi", true],
      ["Otomatik Lig Verisi Sync", true],
      ["Otomatik Puan Durumu", true],
      ["Antrenman & Yoklama", true],
      ["Kulüp Maliyesi", true],
      ["AI Maç Analizi (DR AI Engine)", true],
      ["Akademi Yönetimi", true],
      ["Otomatik Caption (DR AI)", true],
      ["Öncelikli Destek", false],
      ["Özel Eğitim Seansı", false],
      ["Kulübünüzün veri girişi", false],
      ["Oyuncu veri girişi", false],
      ["Gelecekteki özelliklere öncelikli erişim", false],
    ],
  },
  {
    id: "pro",
    name: "PRO",
    tagline: "Profesyonel kulüpler için",
    price: 9999,
    credits: 250,
    icon: Sparkles,
    cta: "Pro ile Üye Ol",
    features: [
      ["Kurumsal Web Sitesi", true],
      ["Özel Domain + SSL", true],
      ["20+ Modül Panel Erişimi", true],
      ["9 AI Instagram Şablonu", true],
      ["Aylık 250 AI Kredi", true],
      ["Otomatik Lig Verisi Sync", true],
      ["Otomatik Puan Durumu", true],
      ["Antrenman & Yoklama", true],
      ["Kulüp Maliyesi", true],
      ["AI Maç Analizi (DR AI Engine)", true],
      ["Akademi Yönetimi", true],
      ["Otomatik Caption (DR AI)", true],
      ["Öncelikli Destek", true],
      ["Özel Eğitim Seansı", true],
      ["Kulübünüzün veri girişi", true],
      ["Oyuncu veri girişi", true],
      ["Gelecekteki özelliklere öncelikli erişim", true],
    ],
  },
];

const VALUE_COMPARISONS = [
  ["Sosyal Medya Yönetim Ajansı", "₺25.000", "Aylık · sınırlı kapsam"],
  ["Bir spor malzeme alımı", "₺5.000", "Tek seferlik"],
  ["Bir grafik tasarımcısı", "₺45.000", "Aylık · sınırlı çıktı"],
  ["DR AI Futbol Plus", "₺4.999", "Aylık · SINIRSIZ özellik"],
];

const PLAN_FAQ = [
  ["Krediler ne için kullanılıyor?", "AI Instagram görseli üretimi (1 görsel = 1 kredi) ve DR AI Engine maç analizleri (1 rapor = 5 kredi)."],
  ["Krediler bir sonraki aya devreder mi?", "Hayır — her ay sıfırlanır. Sınırsız değil ama her plan kulübünüzün aylık ihtiyacının üzerinde tasarlandı."],
  ["Ek kredi alabilir miyim?", "Evet — paketinizin üzerine her zaman ek kredi yükleyebilirsiniz. Birim fiyat: 1 kredi ≈ ₺40."],
  ["Sözleşme süresi nedir?", "Aylık veya yıllık. Yıllık alırsanız 2 ay ücretsiz (1 yılda %16 indirim)."],
  ["Beğenmezsem iade var mı?", "Evet — ilk 30 gün koşulsuz iade. Tek kuruşunuz cebinizde kalır."],
  ["Plan değiştirebilir miyim?", "İstediğiniz zaman, otomatik prorate (kalan günlere göre fark hesabı). Düşürme/yükseltme için 24 saat yeterli."],
];

const PlanCard = ({ plan, i }) => {
  const Icon = plan.icon;
  return (
    <Reveal delay={i}>
      <div
        className={`relative rounded-3xl p-8 h-full flex flex-col transition-all duration-500 ${
          plan.highlight
            ? "bg-gradient-to-b from-dr-yellow/15 via-dr-yellow/5 to-transparent border-2 border-dr-yellow/50 shadow-[0_30px_80px_-20px_rgba(245,220,76,0.4)] lg:scale-105 lg:-my-2"
            : "mkt-card"
        }`}
        data-testid={`plan-${plan.id}`}
      >
        {plan.badge && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <span className="px-4 py-1.5 rounded-full bg-dr-yellow text-dr-black font-hero uppercase text-xs tracking-wider shadow-lg">
              {plan.badge}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <Icon size={28} className={plan.highlight ? "text-dr-yellow" : "text-white/70"} strokeWidth={2} />
          {plan.highlight && <Star size={18} className="text-dr-yellow fill-dr-yellow" />}
        </div>

        <div className="font-hero text-3xl uppercase mb-1">{plan.name}</div>
        <div className="text-sm text-white/55 mb-7">{plan.tagline}</div>

        <div className="mb-7">
          <div className="flex items-end gap-1">
            <span className="font-hero text-6xl text-white">₺{plan.price.toLocaleString("tr-TR")}</span>
            <span className="text-sm text-white/50 mb-2">/ay</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${plan.highlight ? "bg-dr-yellow text-dr-black" : "bg-white/5 text-dr-yellow border border-dr-yellow/20"}`}>
              {plan.credits} AI kredi / ay
            </span>
          </div>
        </div>

        <ul className="space-y-2.5 mb-8 flex-1">
          {plan.features.map(([label, included]) => (
            <li key={label} className={`flex gap-2.5 text-sm leading-relaxed ${included ? "text-white/85" : "text-white/30"}`}>
              {included ? (
                <Check size={16} className="text-dr-yellow shrink-0 mt-0.5" strokeWidth={3} />
              ) : (
                <X size={16} className="text-white/25 shrink-0 mt-0.5" strokeWidth={2.5} />
              )}
              <span className={included ? "" : "line-through"}>{label}</span>
            </li>
          ))}
        </ul>

        <Link
          to={`/uye-ol?plan=${plan.id}`}
          className={`w-full text-center font-hero uppercase tracking-wider text-sm py-3.5 rounded-full transition ${
            plan.highlight
              ? "bg-dr-yellow text-dr-black hover:bg-dr-yellow-hi shadow-[0_15px_40px_-10px_rgba(245,220,76,0.6)]"
              : "border border-white/20 text-white hover:border-dr-yellow hover:text-dr-yellow"
          }`}
          data-testid={`plan-cta-${plan.id}`}
        >
          {plan.cta}
        </Link>
        <a
          href={`https://wa.me/${PHONE_INTL}?text=${encodeURIComponent(`Merhaba, ${plan.name} planı ile ilgileniyorum. Detayları konuşabilir miyiz?`)}`}
          target="_blank" rel="noopener noreferrer"
          className="w-full text-center text-xs text-white/50 hover:text-dr-yellow transition mt-3 inline-flex items-center justify-center gap-1.5"
          data-testid={`plan-wa-${plan.id}`}
        >
          <MessageCircle size={13} /> WhatsApp'tan yaz
        </a>
        <div className="text-center text-[11px] text-white/40 mt-3">
          🛡️ Ücretsiz üyelik · Kurulum ücreti yok
        </div>
      </div>
    </Reveal>
  );
};

export const PricingPage = () => {
  const [open, setOpen] = useState(0);
  return (
    <MarketingLayout>
      <PageHero
        over="ŞEFFAF FİYATLANDIRMA"
        title="KULÜBÜNÜZÜN BÜYÜMESİ"
        accent="HAK ETTİĞİ FİYATLA"
        sub="Kulübünüzü tek panelden yönetin: kadro, fikstür, finans, akademi ve yapay zekâ destekli sosyal medya. Kurumsal web sitesi Pro pakete dahildir."
      />

      {/* Website = Pro-only notice */}
      <section className="px-6 lg:px-10 pt-10 pb-2" data-testid="pricing-website-note">
        <div className="max-w-[1400px] mx-auto">
          <div className="mkt-card rounded-2xl p-5 md:p-6 border border-dr-yellow/30 flex flex-col md:flex-row md:items-center gap-4">
            <span className="shrink-0 px-3 py-1 rounded-full bg-dr-yellow text-dr-black font-hero uppercase text-[11px] tracking-wider">Pro'ya özel</span>
            <p className="text-white/75 text-sm md:text-base">
              <strong className="text-white">Kurumsal web sitesi + özel domain</strong> yalnızca Pro pakete dahildir. Starter ve Plus kulüpleri
              yönetim panelinin tamamını kullanır; paketinizi <strong className="text-dr-yellow">Pro'ya yükselttiğinizde kulübünüzün web sitesini biz açarız</strong>.
            </p>
          </div>
        </div>

      </section>

      {/* Plan cards */}
      <section className="px-6 lg:px-10 pt-8 pb-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-5 items-stretch">
            {PLANS.map((p, i) => <PlanCard key={p.id} plan={p} i={i} />)}
          </div>

          <Reveal delay={3}>
            <p className="text-center text-white/55 text-sm mt-10 max-w-2xl mx-auto">
              <strong className="text-white">Yıllık ödeme</strong> seçerseniz <strong className="text-dr-yellow">2 ay ücretsiz</strong> — yani %16 indirim.
              <br />
              Daha büyük bir kulüp müsünüz? <a href={`https://wa.me/${PHONE_INTL}`} target="_blank" rel="noopener noreferrer" className="text-dr-yellow underline">Özel paket için iletişime geçin</a>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Value comparison — yarı sponsor maaşı */}
      <section className="px-6 lg:px-10 py-20 bg-dr-ink-1/30">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <div className="font-over text-dr-yellow mb-3">DEĞER KARŞILAŞTIRMASI</div>
            <h2 className="mkt-h2 max-w-3xl mx-auto">
              KULÜP YÖNETİMİNDE<br />
              <span className="text-dr-yellow">EN UCUZ KALEM</span>
            </h2>
            <p className="text-white/65 mt-5 max-w-2xl mx-auto">
              Aşağıdaki maliyetlere bakın — DR AI Futbol, kulübünüzün <strong className="text-white">en küçük gideri</strong>, ama
              <strong className="text-dr-yellow"> en büyük getirisi</strong> olur.
            </p>
          </div>

          <div className="space-y-3">
            {VALUE_COMPARISONS.map(([item, price, freq], i) => {
              const isUs = i === VALUE_COMPARISONS.length - 1;
              return (
                <Reveal key={item} delay={i}>
                  <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-6 p-5 rounded-2xl ${
                    isUs ? "bg-dr-yellow/10 border-2 border-dr-yellow/40" : "mkt-card"
                  }`}>
                    <div className="flex items-center gap-4 min-w-0">
                      {isUs ? (
                        <div className="w-10 h-10 rounded-full bg-dr-yellow text-dr-black flex items-center justify-center shrink-0">
                          <Sparkles size={18} strokeWidth={2.5} />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/5 text-white/50 flex items-center justify-center shrink-0">
                          <TrendingUp size={18} />
                        </div>
                      )}
                      <div>
                        <div className={`font-hero uppercase text-base ${isUs ? "text-dr-yellow" : "text-white"}`}>{item}</div>
                        <div className="text-xs text-white/45 mt-0.5">{freq}</div>
                      </div>
                    </div>
                    <div className={`font-hero text-2xl ${isUs ? "text-dr-yellow" : "text-white/85"}`}>{price}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={4}>
            <div className="text-center mt-12 px-6 py-8 rounded-2xl bg-dr-yellow/5 border border-dr-yellow/20">
              <div className="font-hero text-2xl md:text-3xl uppercase mb-2 text-dr-yellow">
                Bir Ajans = Tek Kanal
              </div>
              <div className="font-hero text-2xl md:text-3xl uppercase mb-3">
                DR AI Futbol Plus = 30 gün <span className="text-dr-yellow">SINIRSIZ</span>
              </div>
              <div className="text-white/70 text-sm">
                Üstelik web siteniz, AI görseliniz, maç analiziniz, akademiniz...
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* All-inclusive list */}
      <section className="px-6 lg:px-10 py-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12">
            <div className="font-over text-dr-yellow mb-3">PLUS PLANINDA NELER VAR?</div>
            <h2 className="mkt-h2">
              ŞAŞIRTACAK <span className="text-dr-yellow">12+ ÖZELLİK</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              [Trophy, "Otomatik Puan Durumu"],
              [Brain, "DR AI Engine Maç Analizi"],
              [Activity, "Antrenman & Yoklama"],
              [Users, "Kadro & Sözleşme"],
              [GraduationCap, "Akademi Yönetimi"],
              [Calendar, "Lig Verisi Sync"],
              [Sparkles, "9 AI Şablon Otomatik"],
              [Star, "DR AI Caption + Hashtag"],
              [ShieldCheck, "Bulut Güvenlik + Yedek"],
              [Zap, "Hot Reload Anında Güncelleme"],
              [Heart, "Doğum Günü Hatırlatma"],
              [TrendingUp, "Kulüp Maliyesi Dashboard"],
            ].map(([Icon, t], i) => (
              <Reveal key={t} delay={i % 4}>
                <div className="flex items-center gap-3 p-4 rounded-xl mkt-card">
                  <Icon size={18} className="text-dr-yellow shrink-0" strokeWidth={2} />
                  <span className="text-sm font-semibold text-white/85">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="px-6 lg:px-10 py-20 bg-dr-ink-1/30">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-12">
            <div className="font-over text-dr-yellow mb-3">SIK SORULAN — FİYATLANDIRMA</div>
            <h2 className="mkt-h2">AKLINIZA TAKILANLAR</h2>
          </div>
          <div className="space-y-3">
            {PLAN_FAQ.map(([q, a], i) => (
              <Reveal key={q} delay={i % 3}>
                <div className="mkt-card rounded-xl overflow-hidden">
                  <button onClick={() => setOpen(open === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.03] transition"
                    data-testid={`pricing-faq-${i}`}
                  >
                    <span className="font-semibold text-white text-[15px]">{q}</span>
                    <span className={`text-dr-yellow shrink-0 transition-transform text-xl ${open === i ? "rotate-45" : ""}`}>+</span>
                  </button>
                  {open === i && (
                    <div className="px-5 pb-5 text-white/70 text-sm leading-relaxed">{a}</div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final urgent CTA */}
      <section className="px-6 lg:px-10 py-20">
        <div className="max-w-[1100px] mx-auto rounded-3xl p-10 md:p-16 bg-gradient-to-br from-dr-yellow via-dr-yellow-hi to-dr-yellow text-dr-black mkt-grain relative overflow-hidden">
          <div className="relative text-center">
            <div className="font-over text-dr-black/70 mb-5">SINIRLI ZAMAN AVANTAJI</div>
            <h2 className="font-hero uppercase text-3xl md:text-5xl leading-tight mb-6 text-dr-black">
              BU SEZON KAÇIRACAK<br />
              <span className="underline decoration-4">RAKİP KULÜP OLMAYIN</span>
            </h2>
            <p className="text-dr-black/85 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Her geçen gün, rakipleriniz dijitalleşiyor.
              Kulübünüz hala WhatsApp gruplarında mı koordine oluyor? <strong>Ücretsiz üye olun, farkı ilk haftada görün.</strong>
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href={`https://wa.me/${PHONE_INTL}?text=${encodeURIComponent("Merhaba, fiyatlandırma sayfasından geliyorum. Üyelik hakkında bilgi alabilir miyim?")}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-dr-black text-dr-yellow font-hero uppercase tracking-wider text-sm hover:bg-dr-ink-1 transition shadow-2xl"
                data-testid="pricing-final-wa"
              >
                <MessageCircle size={18} /> WhatsApp · Hemen Yaz
              </a>
              <Link to="/uye-ol"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-dr-black text-dr-black font-hero uppercase tracking-wider text-sm hover:bg-dr-black/10 transition"
                data-testid="pricing-final-demo"
              >
                Ücretsiz Üye Ol <ArrowRight size={16} strokeWidth={3} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};
